import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  isScrolled?: boolean;
}

export function Navbar({ isScrolled: propIsScrolled }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Add Google Fonts
  useEffect(() => {
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm shadow-lg">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Branding Text - Left Side */}
          <div className="flex-shrink-0">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Business Website Pro
            </h2>
            <p className="text-base sm:text-lg text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]" style={{ fontFamily: 'Dancing Script, cursive' }}>
              Innovative Design. No Hidden Fees.
            </p>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 ml-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-semibold transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-[#FF6B6B]'
                    : 'text-white hover:text-[#FF6B6B]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/booking" className="cursor-pointer">
              <button className="px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap bg-[#14B8A6] text-white hover:bg-[#0d9488]">
                Get Started
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ml-auto text-white hover:bg-white/10"
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-sm border-t border-white/10 shadow-xl transition-all duration-300 overflow-hidden ${
        isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              className={`block py-2 font-semibold transition-colors cursor-pointer ${
                location.pathname === link.path
                  ? 'text-[#FF6B6B]'
                  : 'text-white hover:text-[#FF6B6B]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/booking" onClick={() => setIsMenuOpen(false)} className="block cursor-pointer">
            <button className="w-full bg-[#14B8A6] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#0d9488] transition-all duration-300 whitespace-nowrap cursor-pointer">
              Get Started
            </button>
          </Link>
        </div>
      </div>
    </nav>
  );
}
